
import React, { useEffect, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ArticleGrid2 from "@/components/articles/ArticleGrid2";
import FeaturedArticle2 from "@/components/articles/FeaturedArticle2";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, TrendingUp } from "lucide-react";
import { useArticleFiltering2 } from "@/hooks/useArticleFiltering2";
import articles from "@/data/articlesData";
import { getCategoriesArray } from "@/data/articleCategories";
import NewsletterSubscription from "@/components/articles/NewsletterSubscription";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";

const ArtikkelitPage: React.FC = () => {
  const seoData = getEnhancedSeoDataForRoute("/artikkelit");

  const { 
    filteredArticles, 
    visibleArticles, 
    activeCategory, 
    searchTerm,
    setActiveCategory, 
    setSearchTerm, 
    loadMore 
  } = useArticleFiltering2(articles);

  // Performance optimization: memoize expensive calculations
  const shouldShowFeaturedArticle = useMemo(() => {
    return visibleArticles <= 9 && activeCategory === 'all' && !searchTerm && filteredArticles.length > 0;
  }, [visibleArticles, activeCategory, searchTerm, filteredArticles.length]);
  
  const featuredArticleId = useMemo(() => {
    return shouldShowFeaturedArticle ? filteredArticles[0]?.id : undefined;
  }, [shouldShowFeaturedArticle, filteredArticles]);
  
  const categories = useMemo(() => getCategoriesArray(), []);
  
  // Enhanced title generation
  const gridTitle = useMemo(() => {
    if (searchTerm) {
      return `Hakutulokset "${searchTerm}" (${filteredArticles.length})`;
    }
    if (activeCategory === "all") {
      return shouldShowFeaturedArticle ? "Uusimmat artikkelit" : "Kaikki artikkelit";
    }
    const categoryName = categories.find(c => c.id === activeCategory)?.name || activeCategory;
    return `${categoryName} (${filteredArticles.length})`;
  }, [searchTerm, activeCategory, filteredArticles.length, shouldShowFeaturedArticle, categories]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault(); // Prevent page refresh on form submit
  };

  return (
    <>
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/artikkelit"
        schema={seoData.schema}
      />
      
      <div className="bg-white min-h-screen">
        {/* Enhanced hero section */}
        <div className="relative bg-gradient-to-br from-blue-700 via-blue-800 to-blue-900 text-white py-12 md:py-16 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-48 translate-x-48"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-32 -translate-x-32"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center mb-4">
                <TrendingUp className="h-8 w-8 mr-3" />
                <span className="text-blue-200 font-medium">Asiantuntija-artikkelit</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight">
                Laina-asioiden <span className="text-blue-300">Oppaat</span>
              </h1>
              
              <p className="text-lg md:text-xl text-blue-100 mb-8 md:mb-10 leading-relaxed max-w-3xl mx-auto">
                Tutustu asiantuntijoidemme kirjoittamiin oppaisiin lainoista, säästämisestä ja 
                oman talouden hallinnasta. Ajankohtaista tietoa ja käytännön vinkkejä rahoituksesta.
              </p>
              
              {/* Enhanced search form */}
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
                <div className="relative flex-grow">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input 
                    type="search" 
                    placeholder="Etsi artikkeleita (esim. asuntolaina, säästäminen)..." 
                    className="pl-12 pr-4 py-3 bg-white/95 backdrop-blur-sm text-gray-800 border-0 rounded-lg shadow-lg focus:shadow-xl transition-all text-base"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all"
                >
                  Hae
                </Button>
              </form>
              
              {/* Quick stats */}
              <div className="mt-8 flex justify-center items-center space-x-6 text-blue-200 text-sm">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                  {articles.length} artikkelia
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                  {categories.length} kategoriaa
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4 py-8 md:py-12">
          {/* Enhanced category tabs */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Kategoriat
              </h2>
              <span className="text-sm text-gray-500">
                {filteredArticles.length} tulosta
              </span>
            </div>
            
            <div className="overflow-x-auto scrollbar-hide">
              <Tabs 
                value={activeCategory} 
                onValueChange={(value) => setActiveCategory(value as typeof activeCategory)}
                className="w-full"
              >
                <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 p-1 h-auto bg-gray-100 rounded-lg">
                  <TabsTrigger 
                    value="all" 
                    className="py-3 px-4 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium"
                  >
                    Kaikki artikkelit
                  </TabsTrigger>
                  {categories.map(category => (
                    <TabsTrigger 
                      key={category.id} 
                      value={category.id}
                      className="py-3 px-4 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap"
                    >
                      {category.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Featured article - mobile optimized */}
          {shouldShowFeaturedArticle && (
            <FeaturedArticle2 article={filteredArticles[0]} />
          )}

          {/* Article grid with enhanced title */}
          <ArticleGrid2 
            articles={filteredArticles}
            visibleCount={visibleArticles}
            onLoadMore={loadMore}
            skipFirst={shouldShowFeaturedArticle}
            partnerName="Sortter"
            featuredArticleId={featuredArticleId}
            title={gridTitle}
          />
          
          {/* Newsletter subscription */}
          <div className="mt-16">
            <NewsletterSubscription />
          </div>
        </div>
      </div>
    </>
  );
};

export default ArtikkelitPage;
